import {
    Button,
    Collapse,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    makeStyles,
    Menu,
    MenuItem,
    Typography,
} from "@material-ui/core"
import { Add, AddAPhoto, DragIndicator, ExpandMoreOutlined, MoreVert } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import { memo, useCallback, useMemo, useRef, useState } from "react"
import { redimensionarPara300x300 } from "../../../functions/imagens"
import { inativarProduto, vincularImagemProduto } from "../../../services/cardapio"
import useLoadingStore from "../../../stores/LoadingStore"
import { useCardapioContext } from "./CardapioContext"
import DialogConfirmacao from "./DialogConfirmacao"
import DialogEscolherOrigemFoto from "./DialogEscolherOrigemFoto"
import DialogGaleriaFotos from "./DialogGaleriaFotos"
import DisponibilidadeSwitch from "./DisponibilidadeSwitch"
import ItemPasso from "./ItemPasso"
import { TextoEditavel, formatarPreco } from "./TextoEditavel"
import { passoCasaBusca } from "../utils/buscaCardapio"

const ITEM_HEIGHT = 48

const useStyles = makeStyles(theme => ({
    container: {
        width: "100%",
        border: "2px solid #e9ecef",
        borderRadius: 12,
        backgroundColor: "#fafafa",
        padding: "10px",
        transition: "all 0.2s ease",
        "&:hover": {
            borderColor: "#4caf50",
            backgroundColor: "#f8fff8",
        },
        // Indentação por nível (desktop): Produto ocupa ~90% da categoria.
        [theme.breakpoints.up("md")]: {
            width: "90%",
            marginLeft: "auto",
            marginRight: "auto",
        },
    },
    // Destaque do item enquanto está sendo arrastado (sobrepõe o hover).
    containerArrastando: {
        borderColor: "#4caf50 !important",
        backgroundColor: "#e8f5e9 !important",
        boxShadow: "0 0 0 2px rgba(76,175,80,0.5)",
        opacity: 0.9,
    },
    containerImg: {
        margin: "auto",
        maxWidth: "80px",
        height: "80px",
        backgroundColor: "#eee",
        border: "2px dashed #ccc",
        cursor: "pointer",
        overflow: "hidden",
        borderRadius: "10px",
    },
    addAPhoto: {
        margin: "auto",
        display: "block",
        width: "40%",
        height: "40%",
        color: "rgba(0,0,0,0.5)",
    },
    input: {
        display: "none",
    },
    nomeProduto: {
        fontSize: "1.05rem",
        fontWeight: 700,
        color: "#1f2d3d",
        lineHeight: 1.25,
        wordBreak: "break-word",
    },
    descricaoProduto: {
        marginTop: 2,
        fontSize: "0.8rem",
        color: "rgba(0,0,0,0.55)",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
    },
    precoProduto: {
        width: "100%",
        textAlign: "right",
        fontWeight: 700,
        fontSize: "1rem",
        color: "#2e7d32",
        whiteSpace: "nowrap",
    },
    statusProdutoLabel: {
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
    },
    statusAtivo: { color: "#2e7d32" },
    statusInativo: { color: "#c62828" },
    collapse: {
        width: "100%",
    },
    // Cabeçalho da seção que separa o produto dos seus grupos/opções.
    cabecalhoPassos: {
        marginTop: 8,
        marginBottom: 4,
        padding: "6px 10px",
        borderRadius: 8,
        backgroundColor: "#f2f4f5",
        borderBottom: "1px solid #e3e7ea",
    },
    rotuloSecao: {
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "#6b7883",
    },
    botaoAddPasso: {
        textTransform: "none",
        fontWeight: 600,
        color: "#2e7d32",
        whiteSpace: "nowrap",
        padding: "2px 8px",
        "&:hover": { backgroundColor: "rgba(76,175,80,0.08)" },
    },
    noSpinner: {
        "& input[type=number]": {
            MozAppearance: "textfield",
        },
        "& input[type=number]::-webkit-outer-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
        },
        "& input[type=number]::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
        },
    },
}))

const ItemProduto = memo(function ItemProduto({
    conteudo,
    categoria,
    dragHandleProps,
    arrastando,
    termoBusca,
}) {
    const {
        setOpenproduto,
        setModalProduto,
        setCadastrar,
        setModalPasso,
        setOpenPassoDeMontagem,
        setProdutoAtual,
        handleDuplicarProduto,
        handleExcluirProduto,
        handleIdsEditar,
        handleCadastrarImagem,
        buscarFT,
        setCategorias,
        estabelecimento,
    } = useCardapioContext()

    const setLoading = useLoadingStore(s => s.setLoading)
    const { enqueueSnackbar } = useSnackbar()
    const autorizacao = localStorage.getItem("chaveSistema")

    const alertStart = (msg, tipo) => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const [expanded, setExpanded] = useState(false)
    const handleExpandClick = useCallback(() => {
        setExpanded(!expanded)
    }, [expanded])

    // Se a busca casou por algum passo/opção deste produto, abre os passos de
    // montagem automaticamente para o resultado aparecer já visível.
    const termo = (termoBusca ?? "").toLowerCase()
    const buscaAbrePassos =
        !!termo && (conteudo?.opcoes ?? []).some(passo => passoCasaBusca(passo, termo))
    const passosAbertos = expanded || buscaAbrePassos
    // Se o próprio nome do produto casa com o termo, o produto inteiro é o
    // resultado da busca — não faz sentido esconder passos/opções dele. Idem
    // quando o usuário abre o produto manualmente (expanded): mostra tudo.
    const produtoNomeCasa = !!termo && (conteudo?.nome ?? "").toLowerCase().includes(termo)
    const mostrarTodosPassos = produtoNomeCasa || expanded

    // Passos de montagem (grupos de opções) ordenados por `ordemExib`, base da
    // reordenação por arrastar.
    const passosOrdenados = useMemo(() => {
        const o = conteudo?.opcoes ?? []
        return [...o].sort((a, b) => a.ordemExib - b.ordemExib)
    }, [conteudo?.opcoes])

    // Reordena os passos por arrastar (igual a produtos/opções). Reatribui
    // `ordemExib` na ordem visível e marca o produto como editado.
    const draggingPassoRef = useRef(null)
    const [passoArrastadoId, setPassoArrastadoId] = useState(null)

    const handleReordenarPasso = useCallback(
        overIndex => {
            const from = draggingPassoRef.current
            if (from === null || from === overIndex) return
            const arr = [...passosOrdenados]
            const [movido] = arr.splice(from, 1)
            arr.splice(overIndex, 0, movido)
            const reordenados = arr.map((p, idx) => ({ ...p, ordemExib: idx }))
            handleIdsEditar(conteudo.id)
            const visiveisIds = new Set(reordenados.map(p => p.id))
            setCategorias(prevCats =>
                prevCats?.map(cat =>
                    cat.id !== categoria.id
                        ? cat
                        : {
                              ...cat,
                              produtos: cat.produtos.map(prod =>
                                  prod.id !== conteudo.id
                                      ? prod
                                      : {
                                            ...prod,
                                            opcoes: [
                                                ...reordenados,
                                                ...(prod.opcoes ?? []).filter(
                                                    p => !visiveisIds.has(p.id)
                                                ),
                                            ],
                                        }
                              ),
                          }
                )
            )
            draggingPassoRef.current = overIndex
        },
        [passosOrdenados, categoria.id, conteudo.id, setCategorias, handleIdsEditar]
    )

    // Campos editáveis inline em sequência (0=nome, 1=descrição, 2=preço).
    // Tab/Shift+Tab move a edição para o próximo/anterior; fora do intervalo, fecha.
    const [campoAtivo, setCampoAtivo] = useState(null)
    const navegarCampo = useCallback((de, dir) => {
        const prox = de + dir
        setCampoAtivo(prox >= 0 && prox <= 2 ? prox : null)
    }, [])

    const [anchorEl, setAnchorEl] = useState(null)
    const [confirmarExcluir, setConfirmarExcluir] = useState(false)

    const openMoreProduto = Boolean(anchorEl)
    const handleClickMoreProduto = useCallback(
        event => {
            setCadastrar(true)
            setAnchorEl(event.currentTarget)
        },
        [setCadastrar]
    )

    const handleCloseMoreProduto = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const fileInputRef = useRef(null)
    const handleDivClick = useCallback(() => {
        fileInputRef.current.click()
    }, [])

    const handleFileChange = useCallback(
        async event => {
            const file = event.target.files[0]
            if (!file) return

            try {
                const file300 = await redimensionarPara300x300(file)
                const nomeArquivo = file.name
                const idProduto = conteudo.id
                const idOpcao = null
                const idPasso = null

                handleCadastrarImagem(nomeArquivo, idProduto, idPasso, idOpcao, file300)
            } catch (error) {
                alertStart(`${error}`, "error")
            }
        },
        [conteudo.id, handleCadastrarImagem, alertStart]
    )

    const [escolhaAberta, setEscolhaAberta] = useState(false)
    const [galeriaAberta, setGaleriaAberta] = useState(false)

    const handleSelecionarGaleria = useCallback(
        async url => {
            setGaleriaAberta(false)
            setLoading(true)
            try {
                const resp = await vincularImagemProduto(
                    estabelecimento?.id,
                    conteudo.id,
                    null,
                    url,
                    autorizacao
                )
                if (resp?.retornoErro) {
                    alertStart(resp.mensagem || "Não foi possível vincular a imagem.", "error")
                } else {
                    conteudo.urlImagem = url
                    setCategorias(prev => [...prev])
                    alertStart("Imagem atualizada com sucesso", "success")
                }
            } catch (error) {
                console.error(error)
            } finally {
                setLoading(false)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [conteudo, estabelecimento, autorizacao, setCategorias]
    )

    const handleClickOpenProduto = useCallback(() => {
        setOpenproduto(true)
    }, [setOpenproduto])

    const handleChangeDisponibilidadeProduto = useCallback(
        async () => {
            setLoading(true)
            const response = await inativarProduto(conteudo.id, conteudo.ativo, autorizacao)
            if (response.retornoErro) {
                console.error(response)
                alertStart(response.mensagem, "error")
            } else {
                setCategorias(prev =>
                    prev.map(cat =>
                        cat.id !== categoria.id
                            ? cat
                            : {
                                  ...cat,
                                  produtos: cat.produtos.map(prod =>
                                      prod.id !== conteudo.id
                                          ? prod
                                          : { ...prod, ativo: !prod.ativo }
                                  ),
                              }
                    )
                )
                alertStart("Disponibilidade do produto alterada com sucesso", "success")
            }
            setLoading(false)
        },
        [conteudo, categoria, autorizacao, setLoading, alertStart, setCategorias]
    )

    const handleClickPassoDeMontagem = useCallback(() => {
        setOpenPassoDeMontagem(true)
    }, [setOpenPassoDeMontagem])

    const classes = useStyles()

    // const handleDragStart = index => {
    //     setDraggingProduto(index)
    // }
    // const handleDragEnd = () => {
    //     setDraggingProduto(null)
    // }

    // const [draggingOpcoes, setDraggingOpcoes] = useState(null)
    // const handleDragOver = index => {
    //     if (draggingOpcoes === null) return
    //     if (draggingOpcoes === index) return
    //     const itens = [...passos]
    //     const [removido] = itens.splice(draggingOpcoes, 1)
    //     itens.splice(index, 0, removido)
    //     itens.forEach((item, idx) => {
    //         if (item) {
    //             item.ordemExib = idx
    //         }
    //     })
    //     setDraggingOpcoes(index)
    //     setPassos(itens)
    // }

    return (
        <div
            className={`${classes.container} ${arrastando ? classes.containerArrastando : ""}`}
        >
            <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={1}
                    container
                    alignItems="center"
                    justifyContent="center"
                >
                    <IconButton
                        {...dragHandleProps}
                        size="small"
                        title="Arraste para reordenar"
                        aria-label="Arraste para reordenar"
                        style={{ cursor: "grab" }}
                    >
                        <DragIndicator />
                    </IconButton>
                </Grid>
                <Grid
                    className={classes.containerImg}
                    item
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    onClick={() => setEscolhaAberta(true)}
                    xs={12}
                    sm={6}
                    md={1}
                >
                    <Grid container direction="column" justifyContent="center" alignItems="center">
                        {conteudo.urlImagem ? (
                            <img
                                id={`imagemProduto${conteudo.id}`}
                                loading="lazy"
                                style={{
                                    borderRadius: "10px",
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    objectPosition: "center",
                                }}
                                src={conteudo.urlImagem}
                                alt="Imagem"
                            />
                        ) : (
                            <Grid
                                container
                                direction="column"
                                justifyContent="center"
                                alignItems="center"
                            >
                                <AddAPhoto className={classes.addAPhoto} />
                            </Grid>
                        )}
                    </Grid>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className={classes.input}
                        accept="image/*"
                    />
                </Grid>

                <DialogEscolherOrigemFoto
                    open={escolhaAberta}
                    onClose={() => setEscolhaAberta(false)}
                    onDispositivo={() => {
                        setEscolhaAberta(false)
                        handleDivClick()
                    }}
                    onGaleria={() => {
                        setEscolhaAberta(false)
                        setGaleriaAberta(true)
                    }}
                />
                <DialogGaleriaFotos
                    open={galeriaAberta}
                    onClose={() => setGaleriaAberta(false)}
                    onSelect={handleSelecionarGaleria}
                    idEstabelecimento={estabelecimento?.id}
                />

                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={5}
                    container
                    direction="column"
                    justifyContent="center"
                    id="texto"
                >
                    <TextoEditavel
                        valorInicial={conteudo.nome}
                        placeholder="Sem nome"
                        viewClassName={classes.nomeProduto}
                        termoDestaque={termo}
                        ativo={campoAtivo === 0}
                        onAtivar={() => setCampoAtivo(0)}
                        onFechar={() => setCampoAtivo(c => (c === 0 ? null : c))}
                        onNavegar={dir => navegarCampo(0, dir)}
                        onCommit={valor => {
                            conteudo.nome = valor
                            handleIdsEditar(conteudo.id)
                        }}
                    />
                    <TextoEditavel
                        multiline
                        valorInicial={conteudo.descricao}
                        placeholder="Adicionar descrição"
                        viewClassName={classes.descricaoProduto}
                        ativo={campoAtivo === 1}
                        onAtivar={() => setCampoAtivo(1)}
                        onFechar={() => setCampoAtivo(c => (c === 1 ? null : c))}
                        onNavegar={dir => navegarCampo(1, dir)}
                        onCommit={valor => {
                            conteudo.descricao = valor
                            handleIdsEditar(conteudo.id)
                        }}
                    />
                </Grid>
                <Grid
                    container
                    alignItems="center"
                    justifyContent="center"
                    item
                    xs={12}
                    sm={12}
                    md={2}
                >
                    <TextoEditavel
                        valorInicial={conteudo.valor}
                        placeholder="R$ 0,00"
                        viewClassName={classes.precoProduto}
                        formatarView={formatarPreco}
                        inputType="number"
                        inputClassName={classes.noSpinner}
                        inputProps={{
                            style: { textAlign: "right" },
                            step: "0.01",
                            inputMode: "decimal",
                        }}
                        ativo={campoAtivo === 2}
                        onAtivar={() => setCampoAtivo(2)}
                        onFechar={() => setCampoAtivo(c => (c === 2 ? null : c))}
                        onNavegar={dir => navegarCampo(2, dir)}
                        onCommit={valor => {
                            const num = parseFloat(String(valor).replace(",", "."))
                            conteudo.valor = isNaN(num) ? 0 : num
                            handleIdsEditar(conteudo.id)
                        }}
                    />
                </Grid>
                <Grid
                    item
                    container
                    xs={8}
                    sm={8}
                    md={2}
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                >
                    <DisponibilidadeSwitch
                        produto={true}
                        disponivel={conteudo.ativo}
                        handleChangeDisponibilidadeProduto={handleChangeDisponibilidadeProduto}
                    />
                    <Typography
                        component="span"
                        className={`${classes.statusProdutoLabel} ${
                            conteudo.ativo ? classes.statusAtivo : classes.statusInativo
                        }`}
                    >
                        {conteudo.ativo ? "Ativo" : "Inativo"}
                    </Typography>
                </Grid>
                <Grid
                    item
                    container
                    xs={4}
                    sm={4}
                    md={1}
                    alignItems="center"
                    justifyContent="center"
                >
                    <IconButton onClick={handleClickMoreProduto}>
                        <MoreVert fontSize="medium" />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={openMoreProduto}
                        onClose={handleCloseMoreProduto}
                        PaperProps={{
                            style: {
                                maxHeight: ITEM_HEIGHT * 4.5,
                                width: "20ch",
                            },
                        }}
                    >
                        <MenuItem
                            onClick={async () => {
                                handleClickOpenProduto()
                                setModalProduto(conteudo)
                                setCadastrar(false)
                                await buscarFT("produto", conteudo.id, null)
                            }}
                        >
                            Editar Produto
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                handleDuplicarProduto(conteudo.id, categoria.id)
                            }}
                        >
                            Duplicar Produto
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                handleCloseMoreProduto()
                                handleChangeDisponibilidadeProduto()
                            }}
                        >
                            {conteudo.ativo ? "Pausar produto" : "Ativar produto"}
                        </MenuItem>
                        <Divider />
                        <MenuItem
                            style={{ color: "#d32f2f" }}
                            onClick={() => {
                                handleCloseMoreProduto()
                                setConfirmarExcluir(true)
                            }}
                        >
                            Excluir Produto
                        </MenuItem>
                    </Menu>
                    <DialogConfirmacao
                        open={confirmarExcluir}
                        titulo="Excluir produto"
                        mensagem="Tem certeza de que deseja excluir o produto"
                        nomeItem={conteudo.nome}
                        onCancelar={() => setConfirmarExcluir(false)}
                        onConfirmar={() => {
                            setConfirmarExcluir(false)
                            handleExcluirProduto(conteudo.id, categoria.id)
                        }}
                    />
                    <IconButton onClick={handleExpandClick}>
                        <ExpandMoreOutlined />
                    </IconButton>
                </Grid>

                {passosAbertos && (
                    <Collapse
                        className={classes.collapse}
                        in={passosAbertos}
                        timeout="auto"
                        unmountOnExit
                    >
                        <Divider />
                        <Grid container direction="row" spacing={1}>
                            <Grid item xs={12} sm={12} md={12}>
                                <Grid fullWidth>
                                    <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        md={12}
                                        container
                                        alignItems="center"
                                        justifyContent="space-between"
                                        className={classes.cabecalhoPassos}
                                    >
                                        <Typography
                                            component="span"
                                            className={classes.rotuloSecao}
                                        >
                                            Passos de montagem
                                        </Typography>
                                        <Button
                                            size="small"
                                            className={classes.botaoAddPasso}
                                            startIcon={<Add fontSize="small" />}
                                            onClick={() => {
                                                setCadastrar(true)
                                                setModalPasso(null)
                                                handleClickPassoDeMontagem()
                                                setProdutoAtual(conteudo)
                                            }}
                                        >
                                            Adicionar passo
                                        </Button>
                                    </Grid>
                                    <Grid>
                                        <List>
                                            {passosOrdenados.map((opcao, passoIndex) => {
                                                // Durante a busca, esconde os passos que não casam
                                                // com o termo — só o passo com o item procurado
                                                // (ex.: "morango") fica visível. Mas se o match foi
                                                // no nome do produto ou o usuário abriu o produto,
                                                // mostra todos. O índice segue sendo o da lista
                                                // completa (reordenação intacta).
                                                if (
                                                    termo &&
                                                    !mostrarTodosPassos &&
                                                    !passoCasaBusca(opcao, termo)
                                                )
                                                    return null
                                                return (
                                                <ListItem
                                                    key={opcao.id}
                                                    disableGutters
                                                    onDragOver={e => {
                                                        e.preventDefault()
                                                        handleReordenarPasso(passoIndex)
                                                    }}
                                                    onDrop={() => {
                                                        draggingPassoRef.current = null
                                                        setPassoArrastadoId(null)
                                                    }}
                                                    onDragEnd={() => {
                                                        draggingPassoRef.current = null
                                                        setPassoArrastadoId(null)
                                                    }}
                                                >
                                                    <ItemPasso
                                                        opcao={opcao}
                                                        conteudo={conteudo}
                                                        termoBusca={termo}
                                                        arrastando={opcao.id === passoArrastadoId}
                                                        dragHandleProps={{
                                                            draggable: true,
                                                            onDragStart: () => {
                                                                draggingPassoRef.current = passoIndex
                                                                setPassoArrastadoId(opcao.id)
                                                            },
                                                        }}
                                                    />
                                                </ListItem>
                                                )
                                            })}
                                        </List>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Collapse>
                )}
            </Grid>
        </div>
    )
})

export default ItemProduto
